import React from 'react';

export interface StepperProps {
    /** Total number of steps. */
    steps: number;
    /** Active step index (0-based). */
    active: number;
    /** When provided, each step becomes a clickable control. */
    onChange?: (index: number) => void;
    className?: string;
    'aria-label'?: string;
    'aria-labelledby'?: string;
}

const Stepper: React.FC<StepperProps> = ({
    steps,
    active,
    onChange,
    className = '',
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
}) => {
    const segments = Array.from({ length: Math.max(0, steps) }, (_, index) => index);
    // A role is what makes aria-label apply at all, but an unnamed group is pure noise
    // in the a11y tree, so the role only appears alongside a name. progressbar would be
    // wrong either way: its contents are presentational and cannot expose the step buttons.
    const isNamed = Boolean(ariaLabel ?? ariaLabelledBy);

    return (
        <div
            role={isNamed ? 'group' : undefined}
            aria-label={ariaLabel}
            aria-labelledby={ariaLabelledBy}
            className={`flex gap-[4px] ${className}`}
        >
            {segments.map((index) => {
                const isActive = index === active;
                const isCompleted = index < active;
                // Completed steps get a lightened accent, the active step the full accent,
                // upcoming steps a neutral border colour.
                const backgroundColor = isActive
                    ? 'var(--malachite-accent-foreground)'
                    : isCompleted
                      ? 'color-mix(in srgb, var(--malachite-accent-foreground), white 50%)'
                      : 'var(--malachite-card-border)';
                const base = `h-[8px] rounded-full transition-all ${isActive ? 'w-[28px]' : 'w-[16px]'}`;

                if (!onChange) {
                    return <span key={index} className={base} style={{ backgroundColor }} />;
                }

                return (
                    <button
                        key={index}
                        type="button"
                        aria-label={`Go to step ${index + 1}`}
                        aria-current={isActive ? 'step' : undefined}
                        onClick={() => onChange(index)}
                        className={`${base} border-0 p-0 cursor-pointer`}
                        style={{ backgroundColor }}
                    />
                );
            })}
        </div>
    );
};

export default Stepper;
export { Stepper };
